import { useState, useEffect, useCallback } from 'react';

export default function usePdfSearch({ pdfDocumentProxy, setCurrentPage, pageRefs }) {
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]); // Array of { pageNumber, itemIndex }
  const [currentSearchIndex, setCurrentSearchIndex] = useState(-1);
  const [isSearching, setIsSearching] = useState(false);

  const toggleSearch = useCallback(() => {
    setIsSearchOpen((prev) => !prev);
  }, []);

  const executeSearch = useCallback(async (query) => {
    if (!pdfDocumentProxy) return;

    if (!query || !query.trim()) {
      setSearchResults([]);
      setCurrentSearchIndex(-1);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    setSearchResults([]);
    setCurrentSearchIndex(-1);

    try {
      const results = [];
      const totalPages = pdfDocumentProxy.numPages;
      const normalizedQuery = query.toLowerCase();

      for (let i = 1; i <= totalPages; i += 1) {
        const page = await pdfDocumentProxy.getPage(i);
        const textContent = await page.getTextContent();
        const textItems = textContent.items;

        textItems.forEach((item, index) => {
          if (item.str.toLowerCase().includes(normalizedQuery)) {
            results.push({ pageNumber: i, itemIndex: index });
          }
        });
      }

      setSearchResults(results);
      if (results.length > 0) {
        setCurrentSearchIndex(0);
        setCurrentPage(results[0].pageNumber);

        const pageContainer = pageRefs.current[results[0].pageNumber];
        if (pageContainer) {
          pageContainer.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setIsSearching(false);
    }
  }, [pdfDocumentProxy, setCurrentPage, pageRefs]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchQuery.trim()) {
        executeSearch(searchQuery);
      } else {
        setSearchResults([]);
        setCurrentSearchIndex(-1);
        setIsSearching(false);
      }
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery, executeSearch]);

  const handleNextResult = useCallback(() => {
    if (searchResults.length === 0) return;

    const nextIndex = (currentSearchIndex + 1) % searchResults.length;
    setCurrentSearchIndex(nextIndex);

    const result = searchResults[nextIndex];
    setCurrentPage(result.pageNumber);

    const pageContainer = pageRefs.current[result.pageNumber];
    if (pageContainer) {
      pageContainer.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [searchResults, currentSearchIndex, setCurrentPage, pageRefs]);

  const handlePrevResult = useCallback(() => {
    if (searchResults.length === 0) return;

    const prevIndex = (currentSearchIndex - 1 + searchResults.length) % searchResults.length;
    setCurrentSearchIndex(prevIndex);

    const result = searchResults[prevIndex];
    setCurrentPage(result.pageNumber);

    const pageContainer = pageRefs.current[result.pageNumber];
    if (pageContainer) {
      pageContainer.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [searchResults, currentSearchIndex, setCurrentPage, pageRefs]);

  const highlightMatches = useCallback((pageNum) => {
    if (searchResults.length === 0) return;

    const pageWrapper = document.querySelector(`div[data-page-number="${pageNum}"]`);
    if (!pageWrapper) return;

    const pageDiv = pageWrapper.querySelector('.react-pdf__Page');
    if (!pageDiv) return;

    const textLayer = pageDiv.querySelector('.react-pdf__Page__textContent');
    if (!textLayer) return;

    const spans = textLayer.querySelectorAll('span');
    const pageMatches = searchResults.filter((r) => r.pageNumber === pageNum);

    Array.from(spans).forEach((span) => span.classList.remove('pdf-search-highlight'));

    pageMatches.forEach((match) => {
      const span = spans[match.itemIndex];
      if (span) {
        span.classList.add('pdf-search-highlight');
      } else {
        console.warn(`[PDF Highlight] Span not found at index ${match.itemIndex} (Total spans: ${spans.length})`);
      }
    });
  }, [searchResults]);

  useEffect(() => {
    document.querySelectorAll('.pdf-search-highlight').forEach((el) => el.classList.remove('pdf-search-highlight'));

    if (searchResults.length === 0) return;

    const uniquePages = [...new Set(searchResults.map((r) => r.pageNumber))];
    uniquePages.forEach((pageNum) => {
      highlightMatches(pageNum);
    });
  }, [searchResults, highlightMatches]);

  return {
    isSearchOpen,
    searchQuery,
    setSearchQuery,
    searchResults,
    currentSearchIndex,
    isSearching,
    toggleSearch,
    handleNextResult,
    handlePrevResult,
    highlightMatches,
  };
}
